// Must match version.json's `version` (base + build number): the base alone is
// a fixed string like "2.0" in production env files, which would never change
// between deploys and the monitor would never fire.
const CURRENT_APP_VERSION = `${process.env.AMBUNET_APP_VERSION}-${process.env.AMBUNET_BUILD_NUMBER}`

const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000
const RELOAD_ATTEMPT_KEY = 'ambunet-app-version-reload-attempt'

const isLocalDevelopment = () =>
  ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname)

const clearBrowserCaches = async () => {
  if (!('caches' in window)) return

  const cacheNames = await window.caches.keys()
  await Promise.all(cacheNames.map((cacheName) => window.caches.delete(cacheName)))
}

const reloadWithCacheBuster = async (nextVersion) => {
  const attemptedVersion = window.sessionStorage.getItem(RELOAD_ATTEMPT_KEY)
  if (attemptedVersion === nextVersion) return

  window.sessionStorage.setItem(RELOAD_ATTEMPT_KEY, nextVersion)
  await clearBrowserCaches()

  const nextUrl = new URL(window.location.href)
  nextUrl.searchParams.set('ambunetVersion', nextVersion)
  window.location.replace(nextUrl.toString())
}

const fetchDeployedVersion = async () => {
  const response = await fetch(`/version.json?ts=${Date.now()}`, {
    cache: 'no-store',
    headers: {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    },
  })

  if (!response.ok) return null
  const versionInfo = await response.json()
  return versionInfo?.version
}

export const startAppVersionMonitor = () => {
  // The app booted, so the index.html inline chunk-recovery guard (one-shot
  // per session) can be re-armed for any later failure.
  try {
    window.sessionStorage.removeItem('ambunet-chunk-recovery')
  } catch (e) {
    // Storage unavailable - nothing to re-arm.
  }

  if (!CURRENT_APP_VERSION || isLocalDevelopment()) return

  const checkVersion = async () => {
    try {
      const deployedVersion = await fetchDeployedVersion()
      if (!deployedVersion || deployedVersion === CURRENT_APP_VERSION) {
        window.sessionStorage.removeItem(RELOAD_ATTEMPT_KEY)
        return
      }

      await reloadWithCacheBuster(deployedVersion)
    } catch (e) {
      // Version checks should never interrupt normal app usage.
    }
  }

  window.addEventListener('focus', checkVersion)
  window.setInterval(checkVersion, VERSION_CHECK_INTERVAL_MS)
  checkVersion()
}
